import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

// ─── Founder Command cockpit ─────────────────────────────────────────────
// Platform-wide KPIs for the single founder. Reads ONLY the rolled-up
// aggregates returned by GET /api/platform/metrics — it sends zero input and
// has no access to raw user/billing rows. Visibility is gated by `isFounder`
// in CodeOSShell, but that is convenience only: the real wall is requireFounder
// on the endpoint, which 403s a non-founder even if they reach this screen.
//
// This file is PRESENTATION ONLY. The fetch and Metrics shape stay aggregate-
// only; the screen is focused on KPI visibility for the founder.

type Bucket = { today: number; last_7d: number; total: number };

type UsageRow = {
  os_id: string;
  name: string;
  owner_email_name: string | null;
  door: string;
  plan: string;
  used: number;
  cap: number | null;
  period: "lifetime" | "month";
  created_at: string;
};

type Metrics = {
  ok: true;
  generated_at: string;
  signups: Bucket;
  oses: Bucket;
  billing: {
    active_paid: number;
    mrr: number;
    mrr_basis: string;
    plan_distribution: Record<string, number>;
    trial: number;
    paid: number;
    conversion_pct: number;
  };
  usage: UsageRow[];
  doors: { door: string; total: number; activated: number; building: number }[];
  metric_catalog: {
    total_defined: number;
    total_executions: number;
    live_metrics: number;
    top_metrics: {
      key: string;
      label: string;
      type: string | null;
      unit: string | null;
      executions: number;
      workspaces: number;
      last_executed_at: string | null;
    }[];
  };
};

type CommandTab = "overview" | "applications";

type OperatorApplication = {
  id: string;
  full_name: string | null;
  company: string | null;
  email: string;
  vertical: string | null;
  city: string | null;
  country: string | null;
  has_book_of_business: boolean | null;
  clients_90d: number | null;
  prospect_strategy: string | null;
  referral_source: string | null;
  whatsapp: string | null;
  created_at: string;
};

// Plan pills — distinct, subtle color per tier; trial reads as neutral, the
// higher tiers get progressively richer accents (enterprise = gold).
const PLAN_BADGE: Record<string, string> = {
  trial: "bg-zinc-100 text-zinc-500",
  starter: "bg-sky-100 text-sky-700",
  pro: "bg-violet-100 text-violet-700",
  business: "bg-emerald-100 text-emerald-700",
  enterprise: "bg-amber-100 text-amber-800",
};
const PLAN_WEIGHT: Record<string, number> = { trial: 0, starter: 1, pro: 2, business: 3, enterprise: 4 };

function displayPlanName(plan: string): string {
  if (plan === "business") return "Custom Call";
  if (plan === "enterprise") return "Enterprise";
  if (plan === "starter") return "Starter";
  if (plan === "pro") return "Pro";
  return plan;
}

// ─── Usage-row classification ────────────────────────────────────────────
// Two amber attention states drive this screen:
//   near      — capped OS ≥80% consumed → UPGRADE opportunity.
//   idle_paid — a PAID OS with zero usage → CHURN RISK, the single most
//               valuable signal here. Never collapsed, flagged "At risk".
// active/uncapped read healthy (emerald/indigo); idle_trial is noise and is
// the ONLY kind hidden behind the toggle.
type RowKind = "near" | "active" | "uncapped" | "idle_paid" | "idle_trial";

const RANK: Record<RowKind, number> = { near: 0, active: 1, uncapped: 1, idle_paid: 2, idle_trial: 3 };

const KIND_META: Record<RowKind, { chip: string; chipCls: string; barCls: string; rowCls: string }> = {
  near:       { chip: "Near cap", chipCls: "bg-amber-100 text-amber-700",                     barCls: "bg-amber-400",   rowCls: "bg-amber-50/60" },
  idle_paid:  { chip: "At risk",  chipCls: "bg-amber-100 text-amber-800 ring-1 ring-amber-300", barCls: "bg-amber-300", rowCls: "bg-amber-50 border-l-2 border-amber-400" },
  active:     { chip: "Active",   chipCls: "bg-emerald-100 text-emerald-700",                  barCls: "bg-emerald-500", rowCls: "" },
  uncapped:   { chip: "Uncapped", chipCls: "bg-indigo-100 text-indigo-700",                    barCls: "bg-indigo-400",  rowCls: "" },
  idle_trial: { chip: "Idle",     chipCls: "bg-zinc-100 text-zinc-400",                        barCls: "bg-zinc-300",    rowCls: "" },
};

function rowKind(u: UsageRow): RowKind {
  const paid = u.plan !== "trial";
  if (u.used === 0) return paid ? "idle_paid" : "idle_trial";
  if (u.cap !== null && u.cap > 0 && u.used / u.cap >= 0.8) return "near";
  if (u.cap === null) return "uncapped";
  return "active";
}
function rowScore(u: UsageRow, kind: RowKind): number {
  if (kind === "near") return u.cap ? u.used / u.cap : 1; // fullest first
  if (kind === "active" || kind === "uncapped") return u.used; // busiest first
  return PLAN_WEIGHT[u.plan] ?? 0; // idle tiers: heaviest plan first
}
function barPct(u: UsageRow): number {
  if (u.cap === null || u.cap <= 0) return 0;
  return Math.min(100, (u.used / u.cap) * 100);
}

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}
function timeOnly(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function createdLabel(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}
function usdMonthly(n: number): string {
  return `$${n.toLocaleString()}/mo`;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120) || "operator";
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-400 mb-3">{children}</h2>
  );
}

function Stat({
  label, value, sub, hero, accent,
}: { label: string; value: string | number; sub?: string; hero?: boolean; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-5 ${accent ? "border-emerald-200 bg-emerald-50/50" : "border-zinc-200 bg-white"}`}>
      <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-400">{label}</div>
      <div
        className={`mt-1 ${hero ? "font-serif text-3xl leading-tight" : "text-2xl font-semibold"} ${accent ? "text-emerald-700" : "text-zinc-900"}`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${PLAN_BADGE[plan] || PLAN_BADGE.trial}`}>
      {displayPlanName(plan)}
    </span>
  );
}

function UsageRowItem({ u }: { u: UsageRow }) {
  const kind = rowKind(u);
  const meta = KIND_META[kind];
  return (
    <div className={`flex items-start gap-3 px-3 py-1.5 ${meta.rowCls}`}>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="text-sm font-medium leading-tight text-zinc-800 truncate">{u.name}</div>
        {u.owner_email_name && (
          <div className="mt-0.5 text-[10.5px] leading-tight text-zinc-500 truncate">{u.owner_email_name}</div>
        )}
        <div className="mt-0.5 text-[10.5px] leading-tight text-zinc-400">Made {createdLabel(u.created_at)}</div>
      </div>
      <div className="shrink-0 w-28 hidden md:block pt-0.5">
        <span className="block truncate text-[11px] leading-tight text-zinc-600">{u.door}</span>
      </div>
      <div className="shrink-0 w-24 flex pt-0.5">
        <PlanBadge plan={u.plan} />
      </div>
      <div className="shrink-0 w-32 hidden sm:block pt-0.5">
        {u.cap === null ? (
          <div className="text-[11px] text-zinc-500 text-right tabular-nums">{u.used} credits · ∞</div>
        ) : (
          <>
            <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
              <div className={`h-full rounded-full ${meta.barCls}`} style={{ width: `${barPct(u)}%` }} />
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 text-right tabular-nums">{u.used} / {u.cap}</div>
          </>
        )}
      </div>
      <span className={`shrink-0 w-[4.5rem] text-center rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none ${meta.chipCls}`}>
        {meta.chip}
      </span>
    </div>
  );
}

export default function CommandCockpit() {
  const [data, setData] = useState<Metrics | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showIdle, setShowIdle] = useState(false);
  const [tab, setTab] = useState<CommandTab>("overview");
  const [applications, setApplications] = useState<OperatorApplication[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsErr, setAppsErr] = useState<string | null>(null);
  const [slugDrafts, setSlugDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const [providerStatus, setProviderStatus] = useState<Record<string, { soft_disabled: boolean; expires_in_sec: number }> | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/platform/metrics", { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 403 ? "Founder access only" : `Failed (${r.status})`);
        return r.json();
      })
      .then((d) => {
        if (cancelled) return;
        if (d?.error) { setErr(d.error); return; }
        setData(d as Metrics);
      })
      .catch((e) => { if (!cancelled) setErr(e?.message || "Failed to load metrics"); });

    fetch("/api/platform/provider-status", { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled) return;
        if (d?.ok && d?.providers) setProviderStatus(d.providers);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (tab !== "applications") return;
    let cancelled = false;
    setAppsLoading(true);
    setAppsErr(null);
    fetch("/api/platform/operator-applications", { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 403 ? "Founder access only" : `Failed (${r.status})`);
        return r.json();
      })
      .then((d) => {
        if (cancelled) return;
        if (d?.error) {
          setAppsErr(d.error);
          setApplications([]);
          return;
        }
        const ops = Array.isArray(d?.operators) ? (d.operators as OperatorApplication[]) : [];
        setApplications(ops);
        setSlugDrafts((prev) => {
          const next = { ...prev };
          for (const app of ops) {
            if (!next[app.id]) {
              next[app.id] = slugify(app.company || app.full_name || app.email);
            }
          }
          return next;
        });
      })
      .catch((e) => { if (!cancelled) setAppsErr(e?.message || "Failed to load applications"); })
      .finally(() => { if (!cancelled) setAppsLoading(false); });
    return () => { cancelled = true; };
  }, [tab]);

  const approveApplication = async (app: OperatorApplication) => {
    const desiredSlug = (slugDrafts[app.id] || slugify(app.company || app.full_name || app.email)).trim().toLowerCase();
    if (!desiredSlug) {
      setAppsErr("Add a slug before approving.");
      return;
    }
    setSavingId(app.id);
    setAppsErr(null);
    try {
      const r = await fetch(`/api/platform/operator-applications/${app.id}/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ desired_slug: desiredSlug }),
      });
      const payload = await r.json().catch(() => null);
      if (!r.ok) throw new Error(payload?.error || `Failed (${r.status})`);
      setApplications((prev) => prev.filter((row) => row.id !== app.id));
    } catch (e: any) {
      setAppsErr(e?.message || "Failed to approve application");
    } finally {
      setSavingId(null);
    }
  };

  const rejectApplication = async (app: OperatorApplication) => {
    setSavingId(app.id);
    setAppsErr(null);
    try {
      const r = await fetch(`/api/platform/operator-applications/${app.id}/reject`, {
        method: "POST",
        credentials: "include",
      });
      const payload = await r.json().catch(() => null);
      if (!r.ok) throw new Error(payload?.error || `Failed (${r.status})`);
      setApplications((prev) => prev.filter((row) => row.id !== app.id));
    } catch (e: any) {
      setAppsErr(e?.message || "Failed to reject application");
    } finally {
      setSavingId(null);
    }
  };

  if (err) {
    return (
      <div className="px-10 py-8 max-w-5xl">
        <h1 className="text-3xl font-serif text-zinc-900 mb-1">Command</h1>
        <div className="mt-6 rounded-xl border border-red-200 bg-white p-6">
          <div className="text-sm text-red-700">Unable to load platform metrics</div>
          <div className="text-xs text-zinc-500 mt-1">{err}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-10 py-8 max-w-5xl">
        <h1 className="text-3xl font-serif text-zinc-900 mb-1">Command</h1>
        <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-6 text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }

  const dist = data.billing.plan_distribution || {};
  const distEntries = Object.entries(dist).filter(([, n]) => n > 0);

  // Sort + partition the usage list. Default view = everything except idle
  // trials (near-cap → active → idle-PAID). Idle trials collapse behind a
  // toggle; a paid OS never hides.
  const decorated = data.usage
    .map((u) => { const kind = rowKind(u); return { u, kind, rank: RANK[kind], score: rowScore(u, kind), created: new Date(u.created_at).getTime() || 0 }; })
    .sort((a, b) => b.created - a.created || a.rank - b.rank || b.score - a.score || a.u.name.localeCompare(b.u.name));
  const visibleUsage = decorated.filter((d) => d.kind !== "idle_trial");
  const idleTrials = decorated.filter((d) => d.kind === "idle_trial");
  const atRiskCount = decorated.filter((d) => d.kind === "idle_paid").length;
  const nearCount = decorated.filter((d) => d.kind === "near").length;
  const activatedCount = decorated.filter((d) => d.u.used > 0).length;

  return (
    <div className="px-10 py-8 max-w-5xl">
      <header className="mb-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif text-zinc-900">Command</h1>
            <p className="text-sm text-zinc-400 mt-1">
              Platform metrics · generated {formatTime(data.generated_at)}
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-white p-1 text-sm">
            <button
              type="button"
              onClick={() => setTab("overview")}
              className={`rounded-lg px-3 py-1.5 transition-colors ${tab === "overview" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`}
            >
              Overview
            </button>
            <button
              type="button"
              onClick={() => setTab("applications")}
              className={`rounded-lg px-3 py-1.5 transition-colors ${tab === "applications" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`}
            >
              Applications
            </button>
          </div>
        </div>
      </header>

      {tab === "overview" ? (
        <>
          {providerStatus && Object.keys(providerStatus).length > 0 && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
              <div className="text-sm font-medium">
                {Object.entries(providerStatus).map(([p]) => {
                  const name = p.charAt(0).toUpperCase() + p.slice(1);
                  return `${name} unfunded — Operator turns are running on Gemini`;
                }).join(". ")}
              </div>
            </div>
          )}

          <section className="mb-8">
            <SectionLabel>Revenue</SectionLabel>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="MRR" value={usdMonthly(data.billing.mrr)} sub="snapshot · not invoiced" hero accent />
              <Stat label="Conversion" value={`${data.billing.conversion_pct}%`} sub="paid / (paid + trial)" hero />
              <Stat label="Active paid" value={data.billing.active_paid} sub="OSes on a paid plan" />
              <Stat label="Paid / Trial" value={`${data.billing.paid} / ${data.billing.trial}`} sub="live split" />
            </div>
            {distEntries.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {distEntries.map(([plan, n]) => (
                  <span key={plan} className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
                    <PlanBadge plan={plan} /> {n}
                  </span>
                ))}
              </div>
            )}
          </section>

          <section className="mb-8">
            <SectionLabel>Growth</SectionLabel>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Signups" value={data.signups.total} sub={`+${data.signups.today} today · +${data.signups.last_7d} / 7d`} />
              <Stat label="OSes created" value={data.oses.total} sub={`+${data.oses.today} today · +${data.oses.last_7d} / 7d`} />
              <Stat label="Activated OSes" value={activatedCount} sub="OSes with at least one credit" />
              <Stat label="At risk OSes" value={atRiskCount} sub="paid OSes with no usage" />
            </div>
          </section>

          <section className="mb-8">
            <div className="flex items-baseline justify-between mb-3">
              <SectionLabel>Usage vs cap</SectionLabel>
              {(atRiskCount > 0 || nearCount > 0) && (
                <div className="flex items-center gap-2 text-[11px]">
                  {atRiskCount > 0 && (
                    <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold">{atRiskCount} at risk</span>
                  )}
                  {nearCount > 0 && (
                    <span className="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 font-medium">{nearCount} near cap</span>
                  )}
                </div>
              )}
            </div>
            {visibleUsage.length === 0 && idleTrials.length === 0 ? (
              <div className="rounded-xl border border-zinc-200 bg-white p-6 text-sm text-zinc-500">No OSes yet</div>
            ) : (
              <>
                <div className="flex items-center gap-3 px-3 pb-2 text-[10px] uppercase tracking-wider text-zinc-400">
                  <div className="min-w-0 flex-1">OS</div>
                  <div className="shrink-0 w-28 hidden md:block">Door</div>
                  <div className="shrink-0 w-24">Plan</div>
                  <div className="shrink-0 w-32 hidden sm:block text-right">Usage</div>
                  <div className="shrink-0 w-[4.5rem] text-center">Status</div>
                </div>
                <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden divide-y divide-zinc-100">
                  {visibleUsage.map((d) => <UsageRowItem key={d.u.os_id} u={d.u} />)}
                  {showIdle && idleTrials.map((d) => <UsageRowItem key={d.u.os_id} u={d.u} />)}
                </div>
                {idleTrials.length > 0 && (
                  <button
                    onClick={() => setShowIdle((v) => !v)}
                    className="mt-2 text-xs font-medium text-zinc-500 hover:text-zinc-800 transition-colors"
                  >
                    {showIdle ? "Hide idle trials" : `Show ${idleTrials.length} idle trial${idleTrials.length === 1 ? "" : "s"}`}
                  </button>
                )}
              </>
            )}
          </section>

          <section className="mb-8">
            <SectionLabel>Doors</SectionLabel>
            <p className="text-xs text-zinc-400 -mt-1 mb-3">
              Clients grouped by the operator door they came through. "Sloe Labs" = direct signups (no operator).
            </p>
            {(!data.doors || data.doors.length === 0) ? (
              <div className="rounded-xl border border-zinc-200 bg-white p-6 text-sm text-zinc-500">No doors yet</div>
            ) : (
              <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden divide-y divide-zinc-100">
                <div className="flex items-center gap-3 px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-400">
                  <div className="min-w-0 flex-1">Door</div>
                  <div className="shrink-0 w-20 text-right">Clients</div>
                  <div className="shrink-0 w-20 text-right">Activated</div>
                  <div className="shrink-0 w-20 text-right">Building</div>
                </div>
                {data.doors.map((d) => (
                  <div key={d.door} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="min-w-0 flex-1 text-sm font-medium text-zinc-800 truncate">{d.door}</div>
                    <div className="shrink-0 w-20 text-right text-sm font-semibold text-zinc-900 tabular-nums">{d.total}</div>
                    <div className="shrink-0 w-20 text-right text-sm tabular-nums">
                      <span className={d.activated > 0 ? "font-semibold text-emerald-700" : "text-zinc-400"}>{d.activated}</span>
                    </div>
                    <div className="shrink-0 w-20 text-right text-sm tabular-nums text-zinc-500">{d.building}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      ) : (
        <section className="space-y-4">
          <div className="flex items-end justify-between gap-4">
            <div>
              <SectionLabel>Applications</SectionLabel>
              <div className="text-sm text-zinc-500">Founder review queue for new operators.</div>
            </div>
            <div className="text-xs text-zinc-400">
              {appsLoading ? "Loading…" : `${applications.length} pending`}
            </div>
          </div>

          {appsErr && (
            <div className="rounded-xl border border-red-200 bg-white p-4 text-sm text-red-700">{appsErr}</div>
          )}

          {appsLoading ? (
            <div className="rounded-xl border border-zinc-200 bg-white p-6 text-sm text-zinc-500">Loading applications…</div>
          ) : applications.length === 0 ? (
            <div className="rounded-xl border border-zinc-200 bg-white p-6 text-sm text-zinc-500">No pending applications.</div>
          ) : (
            <div className="space-y-3">
              {applications.map((app) => {
                const slugValue = slugDrafts[app.id] || slugify(app.company || app.full_name || app.email);
                return (
                  <div key={app.id} className="rounded-xl border border-zinc-200 bg-white p-4 space-y-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="text-base font-semibold text-zinc-900">{app.company || app.full_name || app.email}</div>
                        <div className="text-sm text-zinc-500">{app.full_name || "—"} · {app.email}</div>
                        <div className="mt-1 text-xs text-zinc-400">
                          {app.vertical || "—"} · {app.city || "—"}{app.country ? `, ${app.country}` : ""} · applied {createdLabel(app.created_at)}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs text-zinc-500">
                        <span className="rounded-full bg-zinc-100 px-2 py-1">Clients 90d: {app.clients_90d ?? 0}</span>
                        <span className="rounded-full bg-zinc-100 px-2 py-1">{app.has_book_of_business ? "Has book" : "No book"}</span>
                        {app.whatsapp && <span className="rounded-full bg-zinc-100 px-2 py-1">{app.whatsapp}</span>}
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-400 mb-1">Desired slug</div>
                        <input
                          value={slugValue}
                          onChange={(e) => setSlugDrafts((prev) => ({ ...prev, [app.id]: e.target.value }))}
                          className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-emerald-400"
                          placeholder="operator-slug"
                        />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-400 mb-1">Referral / strategy</div>
                        <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700 min-h-[42px]">
                          {app.referral_source || "—"}{app.prospect_strategy ? ` · ${app.prospect_strategy}` : ""}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => approveApplication(app)}
                        disabled={savingId === app.id}
                        className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                      >
                        {savingId === app.id ? "Working…" : "Approve"}
                      </button>
                      <button
                        type="button"
                        onClick={() => rejectApplication(app)}
                        disabled={savingId === app.id}
                        className="rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 disabled:opacity-60"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
